/**
 * Formatting utilities for the Closing Disclosure feature
 */

/**
 * Format a number as US currency
 */
export function formatCurrency(
  value: number | string | undefined | null,
  options: {
    showSign?: boolean;
    showCents?: boolean;
    placeholder?: string;
  } = {},
): string {
  const { showSign = false, showCents = true, placeholder = '$0.00' } = options;

  if (value === undefined || value === null || value === '') {
    return placeholder;
  }

  const numericValue =
    typeof value === 'string' ? Number.parseFloat(value.replace(/[,$]/g, '')) : value;

  if (Number.isNaN(numericValue)) {
    return placeholder;
  }

  const absValue = Math.abs(numericValue);
  const formatted = absValue.toLocaleString('en-US', {
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  });

  if (showSign && numericValue < 0) {
    return `-$${formatted}`;
  }

  return `$${formatted}`;
}

/**
 * Parse a currency string to a number
 */
export function parseCurrency(value: string): number {
  const parsed = Number.parseFloat(value.replace(/[,$]/g, ''));
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Format a number as a percentage
 */
export function formatPercentage(
  value: number | string | undefined | null,
  options: {
    decimals?: number;
    placeholder?: string;
  } = {},
): string {
  const { decimals = 3, placeholder = '0%' } = options;

  if (value === undefined || value === null || value === '') {
    return placeholder;
  }

  const numericValue =
    typeof value === 'string' ? Number.parseFloat(value.replace('%', '')) : value;

  if (Number.isNaN(numericValue)) {
    return placeholder;
  }

  return `${numericValue.toFixed(decimals)}%`;
}

/**
 * Parse a percentage string to a number
 */
export function parsePercentage(value: string): number {
  const parsed = Number.parseFloat(value.replace('%', ''));
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Format a date string for display
 */
export function formatDate(
  value: string | Date | undefined | null,
  options: {
    format?: 'short' | 'long' | 'numeric';
    placeholder?: string;
  } = {},
): string {
  const { format = 'numeric', placeholder = '' } = options;

  if (!value) {
    return placeholder;
  }

  const date = typeof value === 'string' ? new Date(value) : value;

  if (Number.isNaN(date.getTime())) {
    return placeholder;
  }

  switch (format) {
    case 'short':
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    case 'long':
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    default:
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
  }
}

/**
 * Format a date for input fields (YYYY-MM-DD)
 */
export function formatDateForInput(value: string | Date | undefined | null): string {
  if (!value) {
    return '';
  }

  const date = typeof value === 'string' ? new Date(value) : value;

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toISOString().split('T')[0] ?? '';
}

/**
 * Normalize a date string to 'yyyy-MM-dd' format
 * Handles both ISO datetime strings (2025-01-01T00:00:00.000Z) and simple date strings (2025-01-01)
 * This is useful when loading dates from the backend which may return full ISO timestamps
 */
export function normalizeDateString(dateValue: string | Date | null | undefined): string {
  if (!dateValue) return '';

  // If it's already in yyyy-MM-dd format, return as-is
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }

  // Parse and format to yyyy-MM-dd
  const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  // Use toISOString and extract the date part to avoid timezone issues
  return date.toISOString().split('T')[0] ?? '';
}

/**
 * Format a line item number with optional letter suffix
 */
export function formatLineNumber(
  section: string,
  number: number | string,
  letter?: string,
): string {
  const num = typeof number === 'string' ? Number.parseInt(number, 10) : number;
  if (Number.isNaN(num)) {
    const strNum = String(number);
    return letter ? `${section}.${strNum}${letter}` : `${section}.${strNum}`;
  }
  const formattedNum = num.toString().padStart(2, '0');
  return letter ? `${section}.${formattedNum}${letter}` : `${section}.${formattedNum}`;
}

/**
 * Format a phone number
 */
export function formatPhoneNumber(value: string | undefined | null): string {
  if (!value) {
    return '';
  }

  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  return value;
}

/**
 * Format an address from parts
 */
export function formatAddress(
  street: string | undefined,
  city: string | undefined,
  state: string | undefined,
  zip: string | undefined,
): string {
  const parts = [street, city && state ? `${city}, ${state}` : city || state, zip].filter(Boolean);
  return parts.join(' ');
}
